import { ReactElement, useMemo } from "react";
import { ItemLayout } from "./ItemLayout";
import {
	DateString,
	Highlight,
	HighlightedText,
	IconAndText,
	PersonAvatar,
	Space
} from "client/Components";
import { useHighlights } from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { CommunityPostGlobalSearchResultItem, GlobalSearchScopeIdentifier } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { CommunityPostImageViewModel } from "client/Api/Communities/communityPostViewer";
import { TimeZone } from "client/DateAndTime/timezones";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { NumberFormatter, useKeyboardListNavigationItem } from "client/Hooks";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { getCommunityPostImageUrl } from "client/Features/Hub/Communities/Composer/common";
import styles from "./CommunityPostItemView.module.less";
import { localize } from "client_legacy/ndaw/culture";
import { getUserProfileImageUrl } from "client/Api/UserProfile/service";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";
import { useCommunityPostNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useCommunityPostNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { Key } from "client/Misc/commonTypes";

export type CommunityPostItemViewProps = {
	item: CommunityPostGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	isMobile: boolean;
	timezone: TimeZone;
};

export const CommunityPostItemView = (props: CommunityPostItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope,
		isMobile,
		timezone
	} = props;

	const { id } = item;

	const executeHandler: () => void = useCommunityPostNavigationHandler({
		id,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("community-post-item", `|${id}`)}
		>
			<CommunityPostItem
				item={item}
				timezone={timezone}
				isMobile={isMobile}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
CommunityPostItemView.displayName = "CommunityPostItemView";

type CommunityPostItemProps = {
	item: CommunityPostGlobalSearchResultItem;
	timezone: TimeZone;
	isMobile: boolean;
	navigationIndex: number;
	onExecute: () => void;
};

const CommunityPostItem = (props: CommunityPostItemProps): ReactElement => {
	const {
		item,
		timezone,
		isMobile,
		navigationIndex,
		onExecute
	} = props;

	const {
		community,
		createdBy,
		messagePreview,
		hitHighlights,
		createdOn,
		statistics,
		images
	} = item;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);
	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(statistics.reactionCounters);

	const image: CommunityPostImageViewModel | undefined = images[0];

	const imageUrl = useMemo(
		(): string | undefined => {
			if (isNil(image)) {
				return undefined;
			}

			return getCommunityPostImageUrl(image.blobId);
		},
		[image]
	);

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(createdBy
		? createdBy
		: {
			displayName: localize("_DeletedUser")
		}
	);

	const avatarPhotoUrl: string | undefined = createdBy && isNotNil(createdBy.avatarId)
		? getUserProfileImageUrl(createdBy.avatarId)
		: undefined;

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
			trackingId="community-post-info"
		>
			<ItemLayout.IconContainer>
				<PersonAvatar
					trackingId="author-avatar"
					initials={initials}
					imageSrc={avatarPhotoUrl}
					size="small"
				/>
			</ItemLayout.IconContainer>
			<ItemLayout.InfoWrapper>
				<ItemLayout.Title trackingId="created-by">
					{computedDisplayName}
				</ItemLayout.Title>

				<ItemLayout.Info>
					<ItemLayout.SubTitle>
						<DateString
							format="RelativeDayWithTime"
							value={createdOn}
							timezone={timezone}
						/>
					</ItemLayout.SubTitle>
					<Space/> | <Space/>
					<ItemLayout.SubTitle>
						{community.name}
					</ItemLayout.SubTitle>
				</ItemLayout.Info>

				<ItemLayout.Content>
					<HighlightedText
						text={messagePreview}
						highlight={highlights.get("messagePreview")}
					/>
				</ItemLayout.Content>

				<ItemLayout.Statistics>
					<IconAndText
						text={statisticsFormatter(reactionStatistic.total)}
					>
						<IconAndText.Icon
							value={reactionStatistic.icon}
							size="small"
						/>
					</IconAndText>
					<IconAndText
						text={statisticsFormatter(statistics.commentsCount)}
					>
						<IconAndText.Icon
							value="comment"
							size="small"
						/>
					</IconAndText>
				</ItemLayout.Statistics>
			</ItemLayout.InfoWrapper>

			{!isMobile && (
				<ItemLayout.Thumbnail>
					{imageUrl && (
						<img
							className={styles.imagePreview}
							src={imageUrl}
							alt={image?.name}
						/>
					)}
				</ItemLayout.Thumbnail>
			)}
		</ItemLayout>
	);
};
CommunityPostItem.displayName = "CommunityPostItem";
